import React, { useState, useEffect } from "react";
import { useNotify } from "react-admin";
import { API, graphqlOperation } from "aws-amplify";
import OutbounddOutlinedIcon from "@material-ui/icons/FastRewindOutlined";
import {
  CircularProgress,
  List,
  IconButton,
  TextField,
  useMediaQuery,
  Grid,
  InputAdornment,
  Backdrop,
} from "@material-ui/core";
import XIcon from "@material-ui/icons/CloseOutlined";
import RefreshIcon from "@material-ui/icons/Refresh";
import { useOutboundContext } from "./context";
import CrewCard from "../../../components/common/CrewCard";
import { AclType, OutboundStatus } from "../../../utils/constants";
import { isAuthorized, logError } from "../../../utils/appUtils";
import useMounted from "../../../utils/useMounted";
import { outboundShipmentByStatus } from "../../../graphql/outbound/custom-queries";
import { ProcessOutboundShipmentItem } from "./ProcessOutboundShipmentItem";

const SearchField = ({ handleSearch, term, classes, handleSubmit, clear }) => (
  <TextField
    id="search"
    name="search"
    size="small"
    variant="outlined"
    margin="dense"
    onChange={handleSearch}
    value={term}
    placeholder="Search"
    className={classes.searchField}
    onKeyDown={handleSubmit}
    InputProps={{
      endAdornment: (
        <InputAdornment position="end">
          <IconButton disableRipple onClick={clear}>
            <XIcon className={classes.clearButton} />
          </IconButton>
        </InputAdornment>
      ),
    }}
  />
);

export const ProcessOutboundShipmentList = (props) => {
  const { state, dispatch } = useOutboundContext();
  const { outboundShipments, loading } = state;
  const [term, setTerm] = useState("");
  const [searchShipments, setSearchShipments] = useState([]);
  const isSmall = useMediaQuery((theme) => theme.breakpoints.down("xs"));
  const { classes, match } = props;
  const isComponentMounted = useMounted();
  const notify = useNotify();

  useEffect(() => {
    init();

    return () => {
      dispatch({ type: "OUTBOUND_SHIPMENTS_LOADED", payload: [] });
      setSearchShipments([]);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const init = async () => {
    if (!loading) {
      dispatch({ type: "SET_LOADING", payload: true });
      try {
        // Only PLACED (load spotted) shipments are workable here; ARRIVED
        // (driver at the gate) shipments live in the Gate Queue page until
        // the crew places them. The GSI takes one status per query.
        const statuses = [OutboundStatus.PLACED];
        if (isAuthorized(AclType.OUTBOUND_VERIFY))
          statuses.push(OutboundStatus.LOADED);

        const responses = await Promise.all(
          statuses.map((status) =>
            API.graphql(
              graphqlOperation(outboundShipmentByStatus, {
                reloadYardId: window.appUser?.reloadYardId,
                status: { eq: status },
                limit: 100,
              })
            )
          )
        );

        const items = responses.flatMap(
          (response) => response.data.outboundShipmentByStatus.items
        );

        const sortStamp = (record) =>
          record.placementDateTime || record.arrivalDateTime || "";
        const data = items.sort((a, b) =>
          sortStamp(a).localeCompare(sortStamp(b))
        );
        setSearchShipments(data);

        if (isComponentMounted) {
          dispatch({
            type: "OUTBOUND_SHIPMENTS_LOADED",
            payload: data,
          });
        }
      } catch (err) {
        logError("ProcessOutboundShipments:init", err, notify);
      } finally {
        dispatch({ type: "SET_LOADING", payload: false });
      }
    }
  };

  const refresh = () => {
    init();
  };

  const handleSearch = (evt) => {
    const value = evt.target.value;
    setTerm(value);
    let searchResult = outboundShipments;
    if (value) {
      const searchTerm = value.toLowerCase();
      searchResult = outboundShipments.filter((itm) => {
        return (
          itm.accountName.toLowerCase().includes(searchTerm) ||
          itm.poNumber.toLowerCase().includes(searchTerm) ||
          itm.pickupNumber.toLowerCase().includes(searchTerm)
        );
      });
    }
    setSearchShipments(searchResult);
  };

  const handleSubmit = (evt) => {
    if (evt.key === "Enter") {
      handleSearch(evt);
    }
  };

  const clear = () => {
    setTerm("");
    setSearchShipments(outboundShipments);
  };

  return (
    <CrewCard
      title={isSmall ? "Outbound" : "Outbound Shipments"}
      icon={<OutbounddOutlinedIcon fontSize="large" />}
      action={
        <div className={classes.topActions}>
          <IconButton onClick={refresh}>
            <RefreshIcon fontSize="large" />
          </IconButton>
        </div>
      }
    >
      <Backdrop className={classes.backdrop} open={loading}>
        <CircularProgress color="inherit" />
      </Backdrop>
      <Grid container>
        <Grid item xs={12}>
          <SearchField
            value={term}
            classes={classes}
            handleSubmit={handleSubmit}
            clear={clear}
            handleSearch={handleSearch}
          />
        </Grid>
        <Grid item xs={12}>
          <List>
            {searchShipments.map((shipment, idx) => (
              <ProcessOutboundShipmentItem
                key={shipment.id}
                shipment={shipment}
                classes={classes}
                match={match}
                idx={idx}
                searchShipments={searchShipments}
              />
            ))}
          </List>
        </Grid>
      </Grid>
    </CrewCard>
  );
};
